import { Skeleton, Tooltip, Typography, TypographyProps } from '@ethings/ui-toolkit';
import { get } from 'lodash';
import { type FC, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { formatDateToDateTime, formatValue, getLastThingEvent } from '../../util';
import { ListPaperColumn } from '../list-item';

interface LastEventDisplayProps {
  feature: string;
  eventTypeId: string;
  thingId: string;
  path?: string;
  loadingComponent?: ReactNode;
  variant?: TypographyProps['variant'];
  formatter?: 'distance' | 'duration' | 'percentage' | 'date' | 'currency' | 'direction' | 'speed';
  disableTooltip?: boolean;
  showEventDate?: boolean;
  label?: string;
}

export const LastEventDisplay: FC<LastEventDisplayProps> = ({ feature, eventTypeId, thingId, loadingComponent: Loading, variant = 'inherit', formatter, path = '', disableTooltip, showEventDate, label }) => {
  const { restClient } = useAuthenticated();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [display, setDisplay] = useState<string>();
  const [hasValue, setHasValue] = useState<boolean>(false);
  const [eventDate, setEventDate] = useState<Date>();
  const { t } = useTranslation();

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    getLastThingEvent(restClient, thingId, { filter: { feature, eventTypeId } })
      .then((event) => {
        if (!isMounted) return;
        const dataFromPath = get(event, path) as unknown;
        const numericValue = typeof dataFromPath === 'number' ? dataFromPath : typeof dataFromPath === 'string' ? parseFloat(dataFromPath) : null;
        const date = get(event, 'occurred') as Date;

        if (date) {
          setEventDate(date);
        }

        if (showEventDate && date) {
          setDisplay(formatDateToDateTime(date));
          setHasValue(true);
        } else if (numericValue !== null && !isNaN(numericValue)) {
          const value = formatValue(numericValue, formatter);
          setDisplay(value);
          setHasValue(true);
        } else {
          setDisplay('');
          setHasValue(false);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setDisplay('');
        setHasValue(false);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [restClient, thingId, feature, eventTypeId, path, formatter, showEventDate]);

  if (isLoading) {
    const loadingContent = !Loading ? <Typography variant={variant}><Skeleton /></Typography> : Loading;
    return label ? <ListPaperColumn label={label}>{loadingContent}</ListPaperColumn> : loadingContent;
  }

  if (label && !hasValue) return null;

  const content = eventDate && !disableTooltip && !showEventDate ? (
    <Tooltip arrow title={`${t('common:common.labels.last-updated')}: ${formatDateToDateTime(eventDate)}`}><Typography variant={variant}>{display}</Typography></Tooltip>
  ) : (
    <Typography variant={variant}>{display}</Typography>
  );

  return label ? <ListPaperColumn label={label}>{content}</ListPaperColumn> : content;
};
